/**
 * Document panel, shared by clients, policies and claims.
 *
 * All three own documents with the same lifecycle, so this is written once and
 * parameterised by scope and document-type enum. Downloads go through the authenticated
 * API rather than a public URL, so an uploaded ID card is not readable by anyone who
 * guesses the path.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { Download, Eye, FileText, Filter as FilterIcon, Image, Trash2, Upload } from 'lucide-react';
import type { DocumentDto } from '@ias/shared';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Skeleton, Tooltip } from '@/components/ui/misc';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EmptyState } from '@/components/common/page-header';
import { DocumentPreviewDialog, isPreviewable } from '@/components/common/document-preview';
import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { useEnumOptions } from '@/components/common/enum-badge';
import { EnumBadge } from '@/components/common/enum-badge';
import { ApiRequestError, download } from '@/lib/api/client';
import { useDeleteDocument, useDocuments, useUploadDocument, type DocumentScope } from '@/lib/api/domains';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import type { EnumName } from '@/lib/registry/enums';
import { cn, formatBytes } from '@/lib/utils';

/** Sentinel for «όλα τα είδη» — not a document type, so it cannot collide with one. */
const ALL_TYPES = '__all__';

/**
 * Below this many attachments the eye finds the file faster than the filter does. Set at the
 * point a single screen stops holding them all: the grid runs three across on a wide viewport,
 * so two full rows still scan at a glance.
 */
const FILTER_THRESHOLD = 6;

export function DocumentPanel({
  scope,
  ownerId,
  typeEnum,
}: {
  scope: DocumentScope;
  ownerId: string;
  typeEnum: EnumName;
}) {
  const { t, dateTime } = useI18n();
  const { can } = useAuth();
  const typeOptions = useEnumOptions(typeEnum);

  const { data, isLoading } = useDocuments(scope, ownerId);
  const uploadDocument = useUploadDocument(scope, ownerId);
  const deleteDocument = useDeleteDocument(scope, ownerId);

  const [documentType, setDocumentType] = React.useState(typeOptions[0]?.value ?? 'other');
  /**
   * Which kind of paperwork is on screen. Separate from `documentType`, which is what the
   * NEXT upload will be filed as — sharing one value would mean picking a filter silently
   * changed what the drop zone was about to create.
   */
  const [typeFilter, setTypeFilter] = React.useState<string>(ALL_TYPES);
  const [dragging, setDragging] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  /**
   * Holds the document being previewed rather than a boolean, because the dialog fetches
   * per `doc.id` — keeping the identity here is what lets it re-fetch when the operator
   * opens a second attachment without closing the first.
   */
  const [previewDoc, setPreviewDoc] = React.useState<DocumentDto | null>(null);

  /**
   * The document awaiting a delete confirmation. Holding the document rather than a flag is
   * what lets the dialog name the file it is about to destroy — "are you sure?" on its own
   * moves the decision without informing it.
   */
  const [deletingDoc, setDeletingDoc] = React.useState<DocumentDto | null>(null);

  const handleFiles = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;

    try {
      await uploadDocument.mutateAsync({ file, fields: { documentType } });
      toast.success(file.name);
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : t('error.generic'));
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const canWrite = can('document:write');

  /*
   * Counts per kind, so the picker can say «Ταυτότητα (4)» — the number is what makes the
   * filter worth opening, and it comes free from a list that is already in memory.
   */
  const countsByType = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const document of data ?? []) {
      counts.set(document.documentType, (counts.get(document.documentType) ?? 0) + 1);
    }
    return counts;
  }, [data]);

  const visible = React.useMemo(
    () => (typeFilter === ALL_TYPES ? (data ?? []) : (data ?? []).filter((d) => d.documentType === typeFilter)),
    [data, typeFilter],
  );

  /*
   * The filter appears only once it would do something: a client with three attachments of
   * two kinds does not need a control, and the office asked for this because some files run
   * to «χαμός», not because every panel should carry one more widget.
   */
  const showFilter = (data?.length ?? 0) > FILTER_THRESHOLD && countsByType.size > 1;

  return (
    <div className="space-y-4">
      {canWrite && (
        <Card>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[12rem] flex-1 space-y-1.5">
                <Label htmlFor="documentType">{t('common.documents')}</Label>
                <Select value={documentType} onValueChange={setDocumentType}>
                  <SelectTrigger id="documentType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {typeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Drag-and-drop with a keyboard-reachable fallback — the label element
                makes the whole zone activate the hidden file input. */}
            <label
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                void handleFiles(event.dataTransfer.files);
              }}
              className={cn(
                'flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed px-4 py-8 text-center transition-colors',
                dragging ? 'border-primary bg-primary/[0.06]' : 'hover:border-primary/40 hover:bg-accent/40',
              )}
            >
              <Upload className="text-muted-foreground size-5" />
              <span className="text-sm font-medium">{t('document.dropzone')}</span>
              <span className="text-muted-foreground text-xs">PDF, JPG, PNG, DOCX · max 15MB</span>
              <input
                ref={inputRef}
                type="file"
                className="sr-only"
                onChange={(event) => void handleFiles(event.target.files)}
                disabled={uploadDocument.isPending}
              />
            </label>

            {uploadDocument.isPending && (
              <p className="text-muted-foreground text-center text-xs">{t('common.loading')}</p>
            )}
          </CardContent>
        </Card>
      )}

      {showFilter && (
        <div className="flex flex-wrap items-center gap-2">
          <FilterIcon className="text-muted-soft size-3.5 shrink-0" />
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="h-8 w-auto min-w-[11rem] text-[13px]" aria-label={t('document.filterByType')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_TYPES}>
                {t('document.allTypes')} ({data?.length ?? 0})
              </SelectItem>
              {/* Only kinds actually present: offering «Διαβατήριο (0)» is a dead end. */}
              {typeOptions
                .filter((option) => countsByType.has(option.value))
                .map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label} ({countsByType.get(option.value)})
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          {typeFilter !== ALL_TYPES && (
            <Button variant="ghost" size="sm" onClick={() => setTypeFilter(ALL_TYPES)}>
              {t('action.clear')}
            </Button>
          )}
        </div>
      )}

      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : !data?.length ? (
        <Card>
          <CardContent>
            <EmptyState title={t('common.empty')} hint={t('common.documents')} compact />
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {visible.map((document) => {
            const isImage = document.mimeType?.startsWith('image/');
            const Icon = isImage ? Image : FileText;
            const canPreview = isPreviewable(document);

            return (
              <Card key={document.id} className="group">
                <CardContent className="flex items-start gap-3 py-3">
                  <div className="bg-muted grid size-9 shrink-0 place-items-center rounded-lg">
                    <Icon className="text-muted-foreground size-4.5" />
                  </div>

                  <div className="min-w-0 flex-1">
                    {/*
                      The file name is the preview trigger, not just a caption. The icon
                      button beside it is the discoverable affordance, but clicking the name
                      is what an operator tries first — and a name that looks clickable and
                      is not is worse than one that never looked clickable at all, so the
                      unpreviewable case stays a plain <p>.
                    */}
                    {canPreview ? (
                      <button
                        type="button"
                        onClick={() => setPreviewDoc(document)}
                        title={document.fileName}
                        className="hover:text-primary block max-w-full truncate text-left text-[13px] font-medium transition-colors duration-150 hover:underline"
                      >
                        {document.fileName}
                      </button>
                    ) : (
                      <p className="truncate text-[13px] font-medium" title={document.fileName}>
                        {document.fileName}
                      </p>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <EnumBadge enum={typeEnum} value={document.documentType} size="sm" />
                      <span className="text-muted-foreground text-[11px] tabular">
                        {formatBytes(document.fileSize)}
                      </span>
                    </div>
                    <p className="text-muted-foreground mt-1 truncate text-[11px]">
                      {dateTime(document.uploadedAt)}
                      {document.uploadedByName ? ` · ${document.uploadedByName}` : ''}
                    </p>
                  </div>

                  <div className="flex shrink-0 flex-col gap-0.5">
                    {canPreview && (
                      <Tooltip content={t('document.preview')}>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => setPreviewDoc(document)}
                          aria-label={t('document.preview')}
                        >
                          <Eye />
                        </Button>
                      </Tooltip>
                    )}
                    <Tooltip content={t('action.download')}>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() =>
                          void download(`/files/${scope}/${document.id}`, document.fileName)
                        }
                        aria-label={t('action.download')}
                      >
                        <Download />
                      </Button>
                    </Tooltip>
                    {can('document:delete') && (
                      <Tooltip content={t('action.delete')}>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => setDeletingDoc(document)}
                          aria-label={t('action.delete')}
                        >
                          <Trash2 className="text-destructive" />
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Mounted only while a document is selected, so unmounting is what revokes the
          object URL — no separate teardown path to forget. */}
      {previewDoc && (
        <DocumentPreviewDialog
          scope={scope}
          doc={previewDoc}
          open
          onOpenChange={(next) => {
            if (!next) setPreviewDoc(null);
          }}
        />
      )}

      {deletingDoc && (
        <ConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) setDeletingDoc(null);
          }}
          title={t('confirm.deleteDocument')}
          subject={deletingDoc.fileName}
          loading={deleteDocument.isPending}
          onConfirm={() => {
            deleteDocument.mutate(deletingDoc.id, {
              // Closing only on success keeps the dialog — and its error toast — in front
              // of the operator when the delete fails, instead of dismissing itself and
              // leaving a document that looks deleted but is not.
              onSuccess: () => setDeletingDoc(null),
            });
          }}
        />
      )}
    </div>
  );
}
